<template>
    <DefaultLayout>
        <template #content>
            <div class="home-container">
                <div class="left">
                    <img :src="variantImage" alt="Illustration" />
                </div>
                <div class="right">
                    <!--Title-->
                    <h2 class="title">
                        {{ t('Pages.Home.Title') }}
                    </h2>
                    <div class="form-container">
                        <!--輸入方框-->
                        <input v-model="shortUrl" class="custom-input" :class="inputClass"
                               :placeholder="t('Pages.Home.InputPlaceholder')"
                               @input="handleInputOnChange"
                               @keydown.enter.prevent="handlerContinueButtonOnClick" />
                        <!--加入按鈕-->
                        <button class="continue-button" type="button" :disabled="isLoading"
                                @click="handlerContinueButtonOnClick">
                            <span>{{ t('Join') }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </template>
    </DefaultLayout>
</template>
<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue'
import { ErrorCodeEnum, ResponseDTO, ShortUrl, ShortUrlRes, VoiceGenderTypeEnum } from '@shared-api'
import { HttpResponse, isEmpty, stringIsEmpty } from '@ipevo-rd/service-web-utils'
import DefaultLayout from '../layouts/DefaultLayout.vue'
import { useInfoStore } from '@shared-store'
import { useLoadingAction } from '@shared-components'
import variantImage from '@shared-assets/images/Variant.svg'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

export default defineComponent({
    name: 'HomePage',
    components: {
        DefaultLayout,
    },
    props: {
        // Short ID
        s: {
            type: String,
            default: null,
        },
    },
    setup(props) {
        // 定義允許的字符集
        const CHARACTER_SET = 'ABCDEFGHJKLMNPRSTUVWXYZ'
        // 定義允許的數字集
        const NUMBER_SET = '23456789'

        // Info Store
        const {
            setShortID,
            setMeetTitle,
            setSupportedTranslationLanguages,
            setUnsupportedTTSLanguages,
            setTargetGender,
            setSessionID,
            setTargetLang,
        } = useInfoStore()
        // 使用 Vue Router
        const router = useRouter()
        // 使用 i18n
        const { t } = useI18n()


        // 會議標題
        const meetTitle = ref<string | null>(null)
        // 短網址
        const shortUrl = ref<string>('')
        // 定義 computed 判斷 class
        const inputClass = computed(() => ({
            'underlined-text': shortUrl.value && shortUrl.value.length > 0,
        }))


        /**
         * 打 API 取得短網址資訊並寫入 store、導頁，交給 Loading 流程等待
         * @return {Promise<void>}
         */
        const fetchShortUrlInfo = async (): Promise<void> => {
            await ShortUrl.getOriginalURL({
                url: shortUrl.value,
            }).then((res: HttpResponse) => {
                // 取得代碼 / 資料 / 訊息
                const { code, data, message } = res as unknown as ResponseDTO
                // 正確取得到結果後
                if (code === ErrorCodeEnum.Success && !isEmpty(data)) {
                    // 取得資料
                    const {
                        // ID
                        id,
                        // 語音性別
                        genders,
                        // 翻譯語言
                        languages,
                        // 會議標題
                        title,
                        // 不支持的TTS語言
                        unsupported_tts_languages,
                        // 當前再線人數
                        size,
                        // 最高能進入人數
                        capacity,
                    } = data as unknown as ShortUrlRes

                    // 當人數達到上限時
                    if (size >= capacity) {
                        //顯示錯誤訊息
                        window.$toast?.({ type: 'error', message: t('Tips.Error.Channel.LimitExceeded') })
                        return
                    }
                    // 設定短網址ID
                    setShortID(shortUrl.value)
                    // 設定標題
                    setMeetTitle(title ?? '')
                    // 設定支持的翻譯語言
                    setSupportedTranslationLanguages(languages)
                    // 設定不支持的語音合成語言
                    setUnsupportedTTSLanguages(unsupported_tts_languages)
                    // 設定Session ID
                    setSessionID(id)
                    // 取得第一筆發音性別
                    const gender = genders?.[0] || VoiceGenderTypeEnum.Female
                    // 設定發音性別
                    setTargetGender(gender)

                    // 翻譯語言只有一個的話
                    if (languages.length === 1) {
                        // 設定目標語言
                        setTargetLang(languages[0])
                        // 跳轉至翻譯頁面
                        router.push({ name: 'tutorial', params: { shortID: shortUrl.value } })
                    } else { // 翻譯語言包含兩個語言以上
                        // 跳轉至語言選擇頁面
                        router.push({ name: 'language' })
                    }
                } else { //發生錯誤時
                    //顯示彈窗
                    window.$toast?.({ type: 'error', message: message })
                }
            })
        }

        // Join 的 Loading 流程：toast、擋重複點、完成後收掉
        const { isLoading, run } = useLoadingAction('Loading.Joining', fetchShortUrlInfo)

        /**
         * 取得短網址資訊；代碼為空只提示、不進 Loading
         * @return {Promise<void>}
         */
        const getShortUrlInfo = async (): Promise<void> => {
            // 檢查短網址是否為空
            if (stringIsEmpty(shortUrl.value)) {
                //顯示彈窗
                window.$toast?.({ type: 'error', message: t('Pages.Home.EnterEventCodeMessage') })
                // 避免空值提交
                return
            }

            // 進入 Loading 流程打 API
            await run()
        }

        /**
         * 處理輸入事件
         * @param event {Event} - 輸入事件
         * @return {void}
         */
        const handleInputOnChange = (event: Event): void => {
            // 取得輸入框
            const target = event.target as HTMLInputElement
            // 將輸入的值轉換為大寫
            const inputValue = target.value.toUpperCase()

            // 定義一個變量來存儲過濾後的值
            let filteredValue = ''
            // 走訪輸入的字串
            for (let i = 0; i < inputValue.length; i++) {
                // 取得每一個字
                const char = inputValue[i]

                // 超過 8 碼
                if (i >= 8) {
                    // 就不執行
                    break
                }

                // 前四個字符只允許 CHARACTER_SET 中的字符
                if (i < 4) {
                    // 字串在 CHARACTER_SET 時
                    if (CHARACTER_SET.includes(char)) {
                        // 將字添加到 filteredValue
                        filteredValue += char
                    }
                } else { //後面的字符只允許 NUMBER_SET 中的字
                    // 字串在 NUMBER_SET 時
                    if (NUMBER_SET.includes(char)) {
                        // 將字添加到 filteredValue
                        filteredValue += char
                    }
                }
            }

            // 更新 shortUrl值
            shortUrl.value = filteredValue
        }

        /**
         * 繼續按鈕點擊事件
         * @return {Promise<void>}
         */
        const handlerContinueButtonOnClick = (): Promise<void> => getShortUrlInfo()


        /**
         * 組件掛載時執行
         * @return {void}
         */
        onMounted(() => {
            // 如果有shortUrl值，則將其賦值給shortUrl
            if (props.s && !stringIsEmpty(props.s)) {
                // 將值設定給 shortUrl
                shortUrl.value = props.s
                // 取得短網址資訊
                getShortUrlInfo()
            }
        })


        return {
            t,
            variantImage,
            isLoading,
            meetTitle,
            shortUrl,
            inputClass,
            getShortUrlInfo,
            handleInputOnChange,
            handlerContinueButtonOnClick,
        }
    },
})
</script>
<style scoped lang="scss">
@use '@shared-assets/scss/variables' as *;

// 輸入框的邊框粗細
$input-border: 2px solid;

// 頁面主容器：窄版直排、可捲動，1280 以上改左右分欄
.home-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: auto;

    .left,
    .right {
        width: 100%;
    }

    .left {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-end;
    }

    .right {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
    }

    .title {
        font-weight: bold;
        text-align: left;
        color: var(--ui-grey-darkest);
        margin: 0;
    }

    .form-container {
        display: flex;
        align-items: center;
    }

    // input 不是區塊容器，text-box-trim 無效，只能用 padding 手動補視覺偏高
    .custom-input {
        padding: calc(1px + 0.265em) 2px 1px;
        border: $input-border var(--ui-grey-lighter);
        border-radius: 16px;
        box-sizing: border-box;
        height: 48px;
        font-size: 20px;
        font-weight: 900;
        text-align: center;
        background: #fff;
        color: var(--ui-grey-darkest);
        outline: none;

        &:hover,
        &:focus {
            border: $input-border var(--ui-ignite);
            outline: none;
        }

        &::placeholder {
            color: var(--ui-grey-lighter);
            font-weight: bold;
        }

        &.underlined-text {
            text-decoration: underline;
            text-decoration-thickness: 2px;
            text-underline-offset: 4px;
        }
    }

    .continue-button {
        background: var(--ui-ignite);
        color: #fff;
        font-size: 20px;
        font-weight: bold;
        line-height: 1;
        height: 48px;
        max-height: 48px;
        margin: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        border: none;
        border-radius: 16px;
        text-transform: capitalize;
        cursor: pointer;

        &:hover {
            filter: brightness(0.95);
        }

        // flex 容器裡的裸文字吃不到 trim，所以文字包一層 span
        @supports (text-box-trim: trim-both) {
            > span {
                text-box-trim: trim-both;
                text-box-edge: cap alphabetic;
            }
        }
    }
}

// mobile <500
@media (max-width: #{$breakpoint-sm - 1}) {
    .home-container {
        .left {
            justify-content: flex-start;
            padding: 48px 40px 0;

            img {
                aspect-ratio: 10 / 9;
                width: 100%;
                margin: 0 auto;
            }
        }

        .right {
            align-items: center;
            height: 100%;
            justify-content: flex-start;
            padding-top: 1.5rem;
        }

        .title {
            font-size: 28px;
            line-height: 32px;
            width: calc(100vw - 80px);
            padding-bottom: 1rem;
        }

        .form-container {
            flex-direction: column;
            align-items: center;
            gap: 12px;
            width: 100%;
        }

        .custom-input,
        .continue-button {
            width: calc(100vw - 80px);
        }
    }
}

// tablet 500–1279
@media (min-width: $breakpoint-sm) and (max-width: #{$breakpoint-lg - 1}) {
    .home-container {
        .left {
            height: 50.12vh;
            justify-content: flex-end;

            img {
                height: 88%;
                margin: 0 auto;
            }
        }

        .right {
            align-items: flex-start;
            padding: 2rem calc((100vw - 480px) / 2) 0;
        }

        .title {
            font-size: 40px;
            line-height: 48px;
        }

        .form-container {
            padding-top: 1.5rem;
            flex-direction: column;
            gap: 12px;
            width: 100%;
        }

        .custom-input,
        .continue-button {
            width: 480px;
            max-width: 100%;
        }
    }
}

// desktop ≥1280：左右分欄
@media (min-width: $breakpoint-lg) {
    .home-container {
        flex-direction: row;
        overflow: hidden;

        .left,
        .right {
            width: 50%;
        }
    }
}

// desktop-lg 1280–1919
@media (min-width: $breakpoint-lg) and (max-width: #{$breakpoint-xl - 1}) {
    .home-container {
        .left img {
            padding-left: 10vw;
            width: calc(300px + 25vw);
            height: calc(270px + 22.5vw);
        }

        .right {
            padding-left: 5vw;
            padding-right: 10vw;
        }

        .title {
            font-size: 48px;
            line-height: 60px;
            width: 35vw;
        }

        .form-container {
            padding-top: 2.5rem;
            flex-direction: column;
            align-items: stretch;
            gap: 20px;
        }

        .custom-input,
        .continue-button {
            width: 300px;
        }
    }
}

// desktop-xl ≥1920
@media (min-width: $breakpoint-xl) {
    .home-container {
        .left img {
            padding-left: 10vw;
            width: 800px;
            height: 720px;
        }

        .right {
            padding-left: min(5vw, 96px);
            padding-right: min(10vw, 192px);
        }

        .title {
            font-size: 72px;
            line-height: 90px;
            width: 35vw;
        }

        .form-container {
            padding-top: 3.75rem;
            flex-direction: row;
            gap: 0;
        }

        .custom-input {
            width: 300px;
        }

        .continue-button {
            width: 200px;
            flex: 0 0 200px;
            margin-left: 12px;
        }
    }
}
</style>
